import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Provider } from "react-redux";
import { store } from "@/store";
import LoginPage from "@/components/auth/LoginPage";
import AdminLayout from "@/components/layout/AdminLayout";
import AdminDashboard from "@/pages/AdminDashboard";
import OrganizationsPage from "@/pages/OrganizationsPage";
import UsersPage from "@/pages/UsersPage";
import SupportRequestsPage from "@/pages/SupportRequestsPage";
import SupportRequestDetailPage from "@/pages/SupportRequestDetailPage";
import SupportIncidentsPage from "@/pages/SupportIncidentsPage";
import SupportIncidentDetailPage from "@/pages/SupportIncidentDetailPage";
import UserDetailPage from "@/pages/UserDetailPage";
import TokenUsagePage from "@/pages/TokenUsagePage";
import ConnectorsPage from "@/pages/ConnectorsPage";
import ConnectorResyncPage from "@/pages/ConnectorResyncPage";
import ToolsPage from "@/pages/ToolsPage";
import BlogPage from "@/pages/BlogPage";
import BlogEditorPage from "@/pages/BlogEditorPage";
import PromptsPage from "@/pages/PromptsPage";
import PromptEditor from "@/pages/PromptEditor";
import ToolEditor from "@/pages/ToolEditor";
import LogsPage from "@/pages/LogsPage";
import AdminProvidersPage from "@/pages/AdminProvidersPage";
import AdminModelsPage from "@/pages/AdminModelsPage";
import ServiceLoginPage from "@/pages/ServiceLoginPage";
import ServiceDashboard from "@/pages/ServiceDashboard";
import ServiceLayout from "@/components/layout/ServiceLayout";
import ServiceEnvVariablesPage from "@/pages/ServiceEnvVariablesPage";
import ServiceAnalyticsPage from "@/pages/ServiceAnalyticsPage";
import ServiceOrganizationsPage from "@/pages/ServiceOrganizationsPage";
import ServiceOrganizationDetailsPage from "@/pages/ServiceOrganizationDetailsPage";
import ServiceApiKeysPage from "@/pages/ServiceApiKeysPage";
import ServiceUsersPage from "@/pages/ServiceUsersPage";
import ServicePromptsPage from "@/pages/ServicePromptsPage";
import ServiceAgentsPage from "@/pages/ServiceAgentsPage";
import ServiceWorkflowBuilder from "@/pages/ServiceWorkflowBuilder";
import ServicePromptEditor from "@/pages/ServicePromptEditor";
import ServiceToolsPage from "@/pages/ServiceToolsPage";
import ServiceToolEditor from "@/pages/ServiceToolEditor";
import ServiceModelProvidersPage from "@/pages/ServiceModelProvidersPage";
import ServiceModelsPage from "@/pages/ServiceModelsPage";
import ServiceModelCostsPage from "@/pages/ServiceModelCostsPage";
import ServiceModelUsagePage from "@/pages/ServiceModelUsagePage";
import { ROUTES } from "@/lib/constants";
import "./App.css";
import { Toaster } from "sonner";
import { AuthProvider, ProtectedRoute } from "./components/auth/AuthContext";
import { ServiceAuthProvider } from "@/contexts/ServiceAuthContext";
import { ServiceProtectedRoute } from "@/components/auth/ServiceProtectedRoute";
import AgentsPage from "./pages/AgentsPage";
import OrganizationDetailsPage from "@/pages/OrganizationDetailsPage";
import AdminEnvPage from "@/pages/AdminEnvPage";
import AdminModelPricingPage from "@/pages/AdminModelPricingPage";
import CostEventsPage from "@/pages/CostEventsPage";
import ProductModelsPage from "@/pages/ProductModelsPage";
import ModelRegistryPage from "@/pages/ModelRegistryPage";
import RoutingHealthPage from "@/pages/RoutingHealthPage";
import WaitlistPage from "@/pages/WaitlistPage";
import ReferralsPage from "@/pages/ReferralsPage";
import EmailAssistantSettingsPage from "@/pages/EmailAssistantSettingsPage";
import CommunicationsPage from "@/pages/communications/CommunicationsPage";
import RateLimitSettingsPage from "@/pages/RateLimitSettingsPage";
import IngestionScopeSettingsPage from "@/pages/IngestionScopeSettingsPage";
import GlobalModelConfigPage from "@/pages/GlobalModelConfigPage";
import WorkerDashboardPage from "@/pages/WorkerDashboardPage";
import GPUMonitoringPage from "@/pages/GPUMonitoringPage";
import OperationLogsPage from "@/pages/OperationLogsPage";
import SlideTracesPage from "@/pages/SlideTracesPage";
import SlideTraceDetailPage from "@/pages/SlideTraceDetailPage";
import FeatureFlagsSettingsPage from "@/pages/FeatureFlagsSettingsPage";
import ConnectorFlagsPage from "@/pages/ConnectorFlagsPage";
import SidebarConfigPage from "@/pages/SidebarConfigPage";
import PlanConfigPage from "@/pages/PlanConfigPage";
import AbilitiesPage from "@/pages/AbilitiesPage"
import NativeAgentsPage from "@/pages/NativeAgentsPage";

// App component with authentication logic
const AppContent = () => {
  // const { isAuthenticated, isLoading } = useAuth()

  // if (isLoading) {
  //   return (
  //     <div className="min-h-screen flex items-center justify-center">
  //       <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
  //     </div>
  //   )
  // }

  return (
    <Router>
      <AuthProvider>
        <ServiceAuthProvider>
          <Toaster richColors position="top-right" style={{ zIndex: 9999 }} />

          <Routes>
            <Route path={ROUTES.LOGIN} element={<LoginPage />} />
            <Route
              path="/admin"
              element={
                <ProtectedRoute requireSuperadmin>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="organizations" element={<OrganizationsPage />} />
              <Route
                path="organizations/:id"
                element={<OrganizationDetailsPage />}
              />
              <Route path="users" element={<UsersPage />} />
              <Route path="users/:id" element={<UserDetailPage />} />
              <Route path="support" element={<SupportRequestsPage />} />
              <Route path="support/incidents" element={<SupportIncidentsPage />} />
              <Route
                path="support/incidents/:id"
                element={<SupportIncidentDetailPage />}
              />
              <Route path="support/:id" element={<SupportRequestDetailPage />} />
              <Route path="token-usage" element={<TokenUsagePage />} />
              <Route path="cost-events" element={<CostEventsPage />} />
              <Route path="connectors" element={<ConnectorsPage />} />
              <Route path="connector-resync" element={<ConnectorResyncPage />} />
              <Route path="providers" element={<AdminProvidersPage />} />
              <Route path="models" element={<AdminModelsPage />} />
              <Route path="tools" element={<ToolsPage />} />
              <Route path="blog" element={<BlogPage />} />
              <Route path="blog/new" element={<BlogEditorPage />} />
              <Route path="blog/:slug" element={<BlogEditorPage />} />
              <Route path="tools/editor/:toolKey" element={<ToolEditor />} />
              <Route
                path="prompts/editor/:identifier"
                element={<PromptEditor />}
              />
              <Route path="prompts" element={<PromptsPage />} />
              <Route path="logs" element={<LogsPage />} />
              <Route path="env" element={<AdminEnvPage />} />
              <Route path="agents" element={<AgentsPage />} />
              <Route path="model-pricing" element={<AdminModelPricingPage />} />
              <Route path="product-models" element={<ProductModelsPage />} />
              <Route path="model-registry" element={<ModelRegistryPage />} />
              <Route path="routing-health" element={<RoutingHealthPage />} />
              <Route path="waitlist" element={<WaitlistPage />} />
              <Route path="referrals" element={<ReferralsPage />} />
              <Route
                path="email-assistant"
                element={<EmailAssistantSettingsPage />}
              />
              <Route path="communications" element={<CommunicationsPage />} />
              <Route path="rate-limits" element={<RateLimitSettingsPage />} />
              <Route
                path="ingestion-scope"
                element={<IngestionScopeSettingsPage />}
              />
              <Route path="model-config" element={<GlobalModelConfigPage />} />
              <Route path="workers" element={<WorkerDashboardPage />} />
              <Route path="gpu-monitoring" element={<GPUMonitoringPage />} />
              <Route path="operation-logs" element={<OperationLogsPage />} />
              <Route path="slide-traces" element={<SlideTracesPage />} />
              <Route
                path="slide-traces/:id"
                element={<SlideTraceDetailPage />}
              />
              <Route
                path="feature-flags"
                element={<FeatureFlagsSettingsPage />}
              />
              <Route
                path="connector-flags"
                element={<ConnectorFlagsPage />}
              />
              <Route path="sidebar-config" element={<SidebarConfigPage />} />
              <Route path="plan-config" element={<PlanConfigPage />} />
              <Route path="abilities" element={<AbilitiesPage />} />
              <Route path="native-agents" element={<NativeAgentsPage />} />
            </Route>
            <Route
              path={ROUTES.HOME}
              element={<Navigate to="/admin" replace />}
            />

            {/* Admin Routes */}
            {/* <Route
            path={ROUTES.ADMIN}
            element={
              // <ProtectedRoute>
                <AdminLayout>
                  <AdminDashboard />
                </AdminLayout>
              // </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.ORGANIZATIONS}
            element={
              // <ProtectedRoute>
                <AdminLayout>
                  <OrganizationsPage />
                </AdminLayout>
              // </ProtectedRoute>
            }
          />


          <Route
          path={ROUTES.HOME}
          element={
            // <ProtectedRoute>
                <AdminLayout>
                  <AdminDashboard />
                </AdminLayout>
              // </ProtectedRoute>
          }
          /> */}

            <Route path={ROUTES.SERVICE_LOGIN} element={<ServiceLoginPage />} />
            <Route
              path="/service"
              element={
                <ServiceProtectedRoute>
                  <ServiceLayout />
                </ServiceProtectedRoute>
              }
            >
              {/* <Route path="dashboard" element={<ServiceDashboard />} /> */}
              <Route
                path="env-variables"
                element={<ServiceEnvVariablesPage />}
              />
              <Route path="analytics" element={<ServiceAnalyticsPage />} />
              <Route
                path="organizations"
                element={<ServiceOrganizationsPage />}
              />
              <Route
                path="organizations/:id"
                element={<ServiceOrganizationDetailsPage />}
              />
              <Route path="api-keys" element={<ServiceApiKeysPage />} />
              <Route path="users" element={<ServiceUsersPage />} />
              <Route path="prompts" element={<ServicePromptsPage />} />
              <Route
                path="prompts/editor/:identifier"
                element={<ServicePromptEditor />}
              />
              <Route path="tools" element={<ServiceToolsPage />} />
              <Route
                path="tools/editor/:toolKey"
                element={<ServiceToolEditor />}
              />
              <Route path="agents" element={<ServiceAgentsPage />} />
              <Route
                path="workflows/new"
                element={<ServiceWorkflowBuilder />}
              />
              <Route
                path="workflows/:id"
                element={<ServiceWorkflowBuilder />}
              />
              <Route
                path="model-providers"
                element={<ServiceModelProvidersPage />}
              />
              <Route path="models" element={<ServiceModelsPage />} />
              <Route path="model-costs" element={<ServiceModelCostsPage />} />
              <Route path="model-usage" element={<ServiceModelUsagePage />} />
            </Route>

            <Route path="*" element={<Navigate to={ROUTES.HOME} replace />} />
          </Routes>
        </ServiceAuthProvider>
      </AuthProvider>
    </Router>
  );
};

// Main App component with Redux Provider
function App() {
  return (
    <Provider store={store}>
      <AppContent />
    </Provider>
  );
}

export default App;
